'use client';

import React, {useState, useMemo, useEffect} from 'react';
import {Box, IconButton, Button, useTheme, Select, MenuItem, FormControl} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {Player} from '@lottiefiles/react-lottie-player';
import searchAnimation from '../../assets/loading/dot-loading.json';
import {useConfig} from "../ConfigProvider";
import {usePostHog} from "posthog-js/react";
import { useFilters } from 'app/providers/filters-provider';

export default function Search(
    {
        setDeals,
        setPage,
        setResultType,
        categories = [],
        fetchDeals,
        initialSearch,
        setParentInputValue
    }) {
    const theme = useTheme();
    const config = useConfig();
    const posthog = usePostHog();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [inputValue, setInputValue] = useState(initialSearch);
    const [isSearching, setIsSearching] = useState(false);
    const { filters, collection, setCollection, isEmptyFilters, setFilters } = useFilters();
    const [selectedCategory, setSelectedCategory] = useState(collection || 'Featured Products');

    // Prepare categories list with Featured Products first
    const allCategories = useMemo(() => {
        const otherCategories = categories?.filter(cat => 
            cat !== 'Featured Products' && cat !== 'Your Categories'
        ) || [];
        return ['Featured Products', ...otherCategories];
    }, [categories]);

    // Sync selectedCategory with collection prop changes
    useEffect(() => {
        if (collection) {
            if (collection !== selectedCategory) {
                setSelectedCategory(collection);
            }
        }
    }, [collection]);

    const resetSearch = async () => {
        // Reset search state
        setDeals([]);
        setPage(1);
        setResultType("filter")
        
        // When resetting, if Featured Products is selected, pass empty string
        // For other categories, pass them as collection
        let effectiveCategory = '';
        if (selectedCategory !== 'Featured Products') {
            effectiveCategory = selectedCategory;
        }
        
        setCollection(selectedCategory);
        
        // Fetch deals for the currently selected category when clearing search
        if (fetchDeals && selectedCategory) {
            await fetchDeals(null, effectiveCategory, null, null, 1, filters);
        }
    }

    const handleCategoryChange = (event) => {
        const newCategory = event.target.value;
        setSelectedCategory(newCategory);
        
        // When changing category, if Featured Products is selected, pass empty string
        // For other categories, pass them as collection
        let effectiveCategory = '';
        if (newCategory !== 'Featured Products') {
            effectiveCategory = newCategory;
        }
        
        setCollection(newCategory);
        
        // Only filter by category, don't automatically search even if there's search input
        if (fetchDeals && newCategory !== selectedCategory) {
            fetchDeals(inputValue.trim(), effectiveCategory, null, null, 1, {});

            if (!isEmptyFilters()) {
                const emptyFilters = {
                    ratingStars: '',
                    ratingCount: '',
                    price: '',
                    discount: '',
                    sort: ''
                };
                setFilters(emptyFilters);
            }
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            const trimmedValue = inputValue.trim();
            if (trimmedValue) {
                setIsSearching(true);
                // When searching, if Featured Products is selected, pass empty string
                // For other categories, pass them as collection
                let effectiveCategory = '';
                if (selectedCategory !== 'Featured Products') {
                    effectiveCategory = trimmedValue;
                }
                setParentInputValue(trimmedValue);
                fetchDeals(trimmedValue, effectiveCategory, null, null, 1, filters).finally(() => {
                    setIsSearching(false);
                });
            } else {
                // When enter is pressed with empty search, show deals for selected category
                setParentInputValue('');
                resetSearch();
            }
        }
    };

    const handleReset = (event) => {
        setInputValue('');
        setParentInputValue('');
        resetSearch();
    };

    const handleSearch = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue) {
            setIsSearching(true);
            // When searching, if Featured Products is selected, pass empty string
            // For other categories, pass them as collection
            let effectiveCategory = '';
            if (selectedCategory !== 'Featured Products') {
                effectiveCategory = selectedCategory;
            }
            setParentInputValue(trimmedValue);
            fetchDeals(trimmedValue, effectiveCategory, null, null, 1, filters).finally(() => {
                setIsSearching(false);
            });
        } else {
            // When search button is clicked with empty search, show deals for selected category
            setParentInputValue('');
            resetSearch();
        }
    };

    return (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, mt: 5}}>
            {/* Category Selector for Mobile */}
            {isMobile && (
                <FormControl sx={{ 
                    width: '90%',
                    mb: 2
                }}>
                    <Select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        displayEmpty
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                            height: '48px',
                            border: '1px solid #E0E0E0',
                            borderRadius: '10px',
                            bgcolor: '#fff',
                            backgroundColor: '#f2f2f2',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '& .MuiSelect-select': {
                                padding: '8px 16px',
                                fontSize: '0.9rem',
                                color: 'var(--theme-dark, #2B4D59)',
                                fontWeight: 500,
                            },
                            '& .MuiSelect-icon': {
                                color: 'var(--theme-color, #436B78)',
                                right: 8,
                                fontSize: '1.2rem'
                            }
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                                    maxHeight: '300px',
                                    width: '90vw',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.9rem',
                                        padding: '8px 16px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(67, 107, 120, 0.1)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'var(--theme-dark, #2B4D59)',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: 'var(--theme-dark, #2B4D59)',
                                                opacity: 0.9
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                    >
                        {allCategories.map((category, index) => (
                            <MenuItem key={index} value={category}>
                                {category}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <Box
                sx={{
                    width: {xs: '90%', sm: '90%', md: '80%', lg: '80%'},
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#fff',
                    border: '1px solid #E0E0E0',
                    borderRadius: '10px',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden'
                }}
            >
                {/* Category Selector for Desktop */}
                {!isMobile && (
                    <FormControl sx={{ 
                        minWidth: 160,
                        flexShrink: 0
                    }}>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={{
                                height: '48px',
                                border: 'none',
                                borderRadius: 0,
                                borderRight: '1px solid #E0E0E0',
                                backgroundColor: '#f2f2f2',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '& .MuiSelect-select': {
                                    padding: '12px 16px',
                                    fontSize: '1rem',
                                    color: 'var(--theme-dark, #2B4D59)',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                },
                                '& .MuiSelect-icon': {
                                    color: 'var(--theme-color, #436B78)',
                                    right: 8,
                                    fontSize: '1.5rem'
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: '8px',
                                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                                        maxHeight: '300px',
                                        minWidth: '200px',
                                        '& .MuiMenuItem-root': {
                                            fontSize: '0.9rem',
                                            padding: '8px 16px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(67, 107, 120, 0.1)',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'var(--theme-dark, #2B4D59)',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: 'var(--theme-dark, #2B4D59)',
                                                    opacity: 0.9
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {allCategories.map((category, index) => (
                                <MenuItem key={index} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Search Input */}
                <InputBase
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={isMobile ? "Search products..." : "Ask anything to find the products you want, e.g. bluetooth speakers with over 5k reviews and 4+ stars..."}
                    inputProps={{
                        'aria-label': 'search',
                        style: {
                            fontSize: isMobile ? '0.85rem' : '1rem',
                        }
                    }}
                    sx={{
                        flex: 1,
                        color: '#1a1a1a',
                        px: isMobile ? 1 : 2,
                        '& .MuiInputBase-input': {
                            '&::placeholder': {
                                color: '#666',
                                opacity: 1,
                                fontSize: isMobile ? '0.8rem' : '1rem'
                            },
                        },
                    }}
                />

                {/* Clear button */}
                {inputValue && (
                    <IconButton
                        onClick={handleReset}
                        aria-label="Clear search"
                        size="small"
                        sx={{
                            mx: isMobile ? 0.5 : 1,
                            color: '#666',
                            padding: isMobile ? '4px' : '8px'
                        }}
                    >
                        <CloseIcon fontSize={isMobile ? "small" : "medium"}/>
                    </IconButton>
                )}

                {/* Search button/icon */}
                {isMobile ? (
                    <Box
                        onClick={!isSearching ? handleSearch : undefined}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isSearching ? '#f8f9fa' : 'var(--theme-dark, #2B4D59)',
                            color: isSearching ? '#666' : 'white',
                            cursor: isSearching ? 'default' : 'pointer',
                            borderTopRightRadius: '10px',
                            borderBottomRightRadius: '10px',
                            width: '48px',
                            height: '48px',
                            transition: 'all 0.2s ease',
                            border: isSearching ? '1px solid #e0e0e0' : 'none',
                            '&:hover': {
                                bgcolor: isSearching ? '#f8f9fa' : 'var(--theme-dark, #2B4D59)',
                                opacity: isSearching ? 1 : 0.9,
                            },
                        }}
                    >
                        {isSearching ? (
                            <Player
                                src={searchAnimation}
                                style={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                loop
                                autoplay
                            />
                        ) : (
                            <SearchIcon fontSize="small"/>
                        )}
                    </Box>
                ) : isSearching ? (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'transparent',
                        borderTopRightRadius: '10px',
                        borderBottomRightRadius: '10px',
                        width: 'auto',
                        height: '48px',
                        px: 2
                    }}>
                        <Player
                            src={searchAnimation}
                            style={{
                                width: 48,
                                height: 48,
                            }}
                            loop
                            autoplay
                        />
                    </Box>
                ) : (
                    <Button
                        onClick={handleSearch}
                        variant="contained"
                        sx={{
                            bgcolor: 'var(--theme-dark, #2B4D59)',
                            color: 'white',
                            borderTopRightRadius: '10px',
                            borderBottomRightRadius: '10px',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            border: 'none',
                            height: '48px',
                            minWidth: '120px',
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                                bgcolor: 'var(--theme-dark, #2B4D59)',
                                opacity: 0.9,
                            },
                        }}
                    >
                        Search
                    </Button>
                )}
            </Box>
        </Box>
    );
} 